// src/pages/cadastros/Setores/SetoresPage.jsx
// (Mamute Fitness) UI rebatizado para "Academias".
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Can } from "../../../components/Can";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useIsMobile } from "../../../hooks/useIsMobile";
import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";
import FiltroBar from "../../../components/FiltroBar/FiltroBar";
import CardListMobile from "../../../templates/mobile/CardListMobile";
import { IconHash, IconUser, IconPhone, IconDocument } from "../../../components/Icons/Icons";
import { useSortableTable, SortableHeader } from "../../../hooks/useSortableTable";
import { pode, nivelUsuario } from "../../../utils/permissoes";

import { db } from "../../../config/firebase";
import { collection, getDocs, query, where, deleteDoc, doc } from "firebase/firestore";

function renderCodigo(a) {
  if (a.codigo) return a.codigo;
  if (a.codigoSequencial != null) {
    return `AC${String(a.codigoSequencial).padStart(3, "0")}`;
  }
  if (a.id) return a.id.slice(0, 8);
  return "-";
}

function getContato(a) {
  return a.contato || a.responsavel || "";
}

function getQtdEquip(a) {
  const v =
    a.quantidadeEquipamentos ??
    a.qtdEquipamentos ??
    a.qtdEquip ??
    a.quantidade ??
    null;

  if (v == null) return "";
  const n = Number(v);
  return Number.isFinite(n) ? n : String(v);
}

function idsEmpresasDoUsuario(usuario) {
  const idsSet = new Set();

  const addId = (v) => {
    if (typeof v === "string") {
      const s = v.trim();
      if (s) idsSet.add(s);
    }
  };

  addId(usuario?.empresaId);
  addId(usuario?.empresaID);
  addId(usuario?.empresa_id);

  (Array.isArray(usuario?.empresasPermitidas) ? usuario.empresasPermitidas : []).forEach(addId);
  (Array.isArray(usuario?.empresasAcesso) ? usuario.empresasAcesso : []).forEach(addId);

  return Array.from(idsSet);
}

function SetoresPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);

  const canCriar = pode(usuarioLogado, "cadastros", "setores", "form");
  const canEditar = pode(usuarioLogado, "cadastros", "setores", "altera");

  const [academias, setAcademias] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [sidebarAberta, setSidebarAberta] = useState(false);

  const [campoFiltro, setCampoFiltro] = useState("nome");
  const [textoFiltro, setTextoFiltro] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const camposFiltro = [
    { value: "codigo", label: "Código" },
    { value: "nome", label: "Academia" },
    { value: "empresaNome", label: "Cliente" },
    { value: "contato", label: "Contato" },
    { value: "telefone", label: "Telefone" },
    { value: "email", label: "Email" },
  ];

  useEffect(() => {
    async function carregarAcademias() {
      if (!usuarioLogado) return;

      setCarregando(true);
      setErro("");

      try {
        const setoresRef = collection(db, "setores");
        const grupoId = usuarioLogado?.grupoId || null;

        const nUser = nivelUsuario(usuarioLogado);
        const isAdminLike = nUser >= 5; // Admin/Gestor
        const isGlobal =
          usuarioLogado?.globalAccess === true ||
          usuarioLogado?.acessoGlobal === true;

        let lista = [];

        if (isAdminLike || isGlobal) {
          // Admin/global: pode listar por grupo
          const q = grupoId
            ? query(setoresRef, where("grupoId", "==", grupoId))
            : query(setoresRef, where("grupoId", "==", "__nulo__"));

          const snap = await getDocs(q);
          lista = snap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }));
        } else {
          // Não-admin: busca somente pelas empresas permitidas (evita query que pode falhar por PERMISSION_DENIED)
          const empresaIds = idsEmpresasDoUsuario(usuarioLogado);
          const map = new Map();

          for (const empId of empresaIds) {
            if (!empId) continue;
            const q = grupoId
              ? query(setoresRef, where("grupoId", "==", grupoId), where("empresaId", "==", empId))
              : query(setoresRef, where("empresaId", "==", empId));

            const snap = await getDocs(q);
            snap.docs.forEach((d) => {
              map.set(d.id, { id: d.id, ...d.data() });
            });
          }

          lista = Array.from(map.values());
        }

        setAcademias(lista);
      } catch (e) {
        console.error("Erro ao carregar academias:", e);
        setErro("Erro ao carregar academias. Tente novamente.");
      } finally {
        setCarregando(false);
      }
    }

    carregarAcademias();
  }, [usuarioLogado]);

  const academiasFiltradas = academias.filter((a) => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return true;

    let valorBase = "";

    if (campoFiltro === "codigo") {
      valorBase = renderCodigo(a);
    } else if (campoFiltro === "contato") {
      valorBase = getContato(a);
    } else {
      valorBase = a[campoFiltro] ?? "";
    }

    return String(valorBase).toLowerCase().includes(texto);
  });

  const { sortedData, requestSort, getSortDirection } = useSortableTable(
    academiasFiltradas,
    "nome"
  );

  async function handleExcluir(academiaId) {
    if (!academiaId) return;

    const confirmar = window.confirm(
      "Tem certeza que deseja excluir esta academia? Essa ação não pode ser desfeita."
    );
    if (!confirmar) return;

    setCarregando(true);
    setErro("");

    try {
      console.log("Deletando academia:", academiaId);
      await deleteDoc(doc(db, "setores", academiaId));
      console.log("Academia deletada com sucesso!");
      setAcademias((prev) => prev.filter((a) => a.id !== academiaId));
    } catch (e) {
      console.error("❌ Erro ao excluir academia:", {
        code: e.code,
        message: e.message,
        fullError: e.toString(),
      });
      
      // Extrair mensagem de erro específica
      let msgErro = "Erro ao excluir academia. Tente novamente.";
      if (e.code === "permission-denied") {
        msgErro = "❌ Permissão negada. Você não tem acesso para deletar esta academia.";
      } else if (e.code === "not-found") {
        msgErro = "Academia não encontrada no banco de dados.";
      } else if (e.code === "failed-precondition") {
        msgErro = "Não é possível deletar: há dados vinculados (máquinas, agendamentos, etc).";
      } else if (e.message) {
        msgErro = `Erro: ${e.message}`;
      }
      
      setErro(msgErro);
    } finally {
      setCarregando(false);
    }
  }

  const itemsParaCards = academiasFiltradas.map((a) => ({
    id: a.id,
    nome: a.nome || "Sem nome",
    subtitulo: a.empresaNome || "-",
    detalhes: [
      { icone: <IconHash size="14px" />, label: "Código", valor: renderCodigo(a) },
      { icone: <IconUser size="14px" />, label: "Contato", valor: getContato(a) || "-" },
      { icone: <IconPhone size="14px" />, label: "Telefone", valor: a.telefone || "-" },
    ],
  }));

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Academias"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
        abaBottom="cadastros"
      >
        <CardListMobile
          items={itemsParaCards}
          carregando={carregando}
          erro={erro}
          carregandoMessage="Carregando academias..."
          titulo="Academias"
          searchPlaceholder="Buscar academia..."
          novoLabel="Nova Academia"
          emptyMessage="Nenhuma academia encontrada"
          onHome={() => navigate("/")}
          onNovo={canCriar ? () => navigate("/cadastros/setores/novo") : undefined}
          onEditar={canEditar ? (id) => navigate(`/cadastros/setores/editar/${id}`) : undefined}
          getSearchText={(item) => `${item.nome} ${item.subtitulo}`}
        />
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop tema={tema} titulo="Academias" subtitulo="">
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Academias cadastradas</h3>
            <p>Cadastro de academias vinculadas a um cliente (empresa) do grupo atual.</p>
          </div>

          <div className="pc-table-header-actions">
            {canCriar && (
              <Can modulo="cadastros" recurso="setores" acao="form">
                <button
                  type="button"
                  className="pc-btn-primario"
                  onClick={() => navigate("/cadastros/setores/novo")}
                >
                  + Nova Academia
                </button>
              </Can>
            )}
          </div>
        </header>

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          onAdicionarClick={() => navigate("/cadastros/setores/novo")}
          addLabel="+ Nova Academia"
          placeholder="Filtrar academias..."
          mostrarAdicionar={false}
        />

        <div className="pc-table-wrapper">
          {erro && (
            <div
              style={{
                background: "#7f1d1d",
                color: "#fecaca",
                padding: 8,
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          {carregando ? (
            <p style={{ padding: 10 }}>Carregando academias...</p>
          ) : sortedData.length === 0 ? (
            <p style={{ padding: 10 }}>Nenhuma academia encontrada.</p>
          ) : (
            <table className="pc-table pc-table-expandable">
              <thead>
                <tr>
                  <th style={{ width: 44 }} />
                  <SortableHeader
                    label="Código"
                    sortKey="codigoSequencial"
                    onSort={requestSort}
                    sortDirection={getSortDirection("codigoSequencial")}
                  />
                  <SortableHeader
                    label="Academia"
                    sortKey="nome"
                    onSort={requestSort}
                    sortDirection={getSortDirection("nome")}
                  />
                  <SortableHeader
                    label="Cliente"
                    sortKey="empresaNome"
                    onSort={requestSort}
                    sortDirection={getSortDirection("empresaNome")}
                  />
                  <SortableHeader
                    label="Contato"
                    sortKey="contato"
                    onSort={requestSort}
                    sortDirection={getSortDirection("contato")}
                  />
                  <SortableHeader
                    label="Telefone"
                    sortKey="telefone"
                    onSort={requestSort}
                    sortDirection={getSortDirection("telefone")}
                  />
                  <SortableHeader
                    label="Qtd. Equip."
                    sortKey="quantidadeEquipamentos"
                    onSort={requestSort}
                    sortDirection={getSortDirection("quantidadeEquipamentos")}
                  />
<th style={{ textAlign: "right" }}>Ações</th>
                </tr>
              </thead>

              <tbody>
                {sortedData.map((a) => {
                  const aberto = expandedId === a.id;

                  return (
                    <React.Fragment key={a.id}>
                      <tr className={aberto ? "pc-row-open" : undefined}>
                        <td>
                          <button
                            type="button"
                            className="pc-expand-btn"
                            aria-label={aberto ? "Recolher" : "Expandir"}
                            onClick={() => setExpandedId((prev) => (prev === a.id ? null : a.id))}
                          >
                            <span className={aberto ? "pc-expand-chevron open" : "pc-expand-chevron"}>
                              ▸
                            </span>
                          </button>
                        </td>

                        <td>{renderCodigo(a)}</td>
                        <td style={{ fontWeight: 600 }}>{a.nome || "-"}</td>
                        <td>{a.empresaNome || "-"}</td>
                        <td>{getContato(a) || "-"}</td>
                        <td>{a.telefone || "-"}</td>
                        <td style={{ textAlign: "right" }}>{getQtdEquip(a) !== "" ? getQtdEquip(a) : "-"}</td>
<td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          <Can modulo="cadastros" recurso="setores" acao="altera">
                            <button
                              type="button"
                              className="pc-icon-btn"
                              title="Editar academia"
                              onClick={() => navigate(`/cadastros/setores/editar/${a.id}`)}
                            >
                              ✏️
                            </button>
                          </Can>

                          <Can modulo="cadastros" recurso="setores" acao="deleta">
                            <button
                              type="button"
                              className="pc-icon-btn"
                              title="Apagar academia"
                              onClick={() => handleExcluir(a.id)}
                            >
                              🗑️
                            </button>
                          </Can>
                        </td>
                      </tr>

                      {aberto && (
                        <tr className="pc-expanded-row">
                          <td colSpan={8}>
                            <div className="pc-expand-panel">
                              <div className="pc-expand-grid">
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Cliente</div>
                                  <div className="pc-expand-value">{a.empresaNome || "-"}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Contato</div>
                                  <div className="pc-expand-value">{getContato(a) || "-"}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Telefone</div>
                                  <div className="pc-expand-value">{a.telefone || "-"}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Email</div>
                                  <div className="pc-expand-value">{a.email || "-"}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Qtd. Equip.</div>
                                  <div className="pc-expand-value">{getQtdEquip(a) !== "" ? getQtdEquip(a) : "-"}</div>
                                </div>
<div className="pc-expand-item">
                                  <div className="pc-expand-label">Endereço</div>
                                  <div className="pc-expand-value">{a.endereco || "-"}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Código</div>
                                  <div className="pc-expand-value">{renderCodigo(a)}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">ID</div>
                                  <div className="pc-expand-value">{a.id}</div>
                                </div>
                              </div>

                              <div style={{ marginTop: 10 }}>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label" style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                    <IconDocument size="14px" /> Observações
                                  </div>
                                  <div className="pc-expand-value">{a.observacoes || a.obs || "-"}</div>
                                </div>
                              </div>

                              <div className="pc-expand-actions">
                                <Can modulo="cadastros" recurso="setores" acao="altera">
                                  <button
                                    type="button"
                                    className="pc-btn-secundario"
                                    onClick={() => navigate(`/cadastros/setores/editar/${a.id}`)}
                                  >
                                    Editar
                                  </button>
                                </Can>

                                <Can modulo="cadastros" recurso="setores" acao="deleta">
                                  <button
                                    type="button"
                                    className="pc-btn-perigo"
                                    onClick={() => handleExcluir(a.id)}
                                  >
                                    Excluir
                                  </button>
                                </Can>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default SetoresPage;
